import { useState } from 'react'
import { useRouter } from 'next/router'
import { useTranslations } from 'next-intl'
import {
  FormControl,
  FormLabel,
  FormErrorMessage,
  Input,
  Stack,
  Button,
  useColorModeValue,
  VStack,
  InputGroup,
  InputRightElement,
  Box,
  Text,
  Link,
  Alert,
  AlertIcon,
  AlertDescription,
} from '@chakra-ui/react'
import NextLink from 'next/link'
import { useForm } from 'react-hook-form'

import { useLogin } from '@/hooks/useAuthActions'

type FormValues = {
  email: string
  password: string
}

export function SignInForm() {
  const t = useTranslations()
  const router = useRouter()
  const [show, setShow] = useState(false)
  const handlePasswordVisibility = () => setShow(!show)

  const [loginFunction, { error, loading }] = useLogin(router)

  const {
    handleSubmit,
    register,
    formState: { errors },
  } = useForm<FormValues>()

  function onSubmit(values: FormValues) {
    const { email, password } = values
    loginFunction(email, password)
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <VStack spacing="4">
        <FormControl id="email" isInvalid={!!errors?.email}>
          <FormLabel>{t('auth.form.email')}</FormLabel>
          <Input
            type="email"
            {...register('email', {
              required: t('forms.errors.is-required'),
            })}
          />
          <FormErrorMessage>
            {errors.email && errors.email.message}
          </FormErrorMessage>
        </FormControl>
        <FormControl id="password" isInvalid={!!errors?.password}>
          <FormLabel>{t('auth.form.password')}</FormLabel>
          <InputGroup size="md">
            <Input
              type={show ? 'text' : 'password'}
              {...register('password', {
                required: t('forms.errors.is-required'),
              })}
            />
            <InputRightElement width="4.5rem">
              <Button
                h="1.75rem"
                size="sm"
                bg={useColorModeValue('gray.300', 'gray.700')}
                _hover={{
                  bg: useColorModeValue('gray.400', 'gray.800'),
                }}
                onClick={handlePasswordVisibility}
              >
                {show ? t('buttons.hide') : t('buttons.show')}
              </Button>
            </InputRightElement>
          </InputGroup>
          <FormErrorMessage>
            {errors.password && errors.password.message}
          </FormErrorMessage>
        </FormControl>
        <Stack direction="row" justify="end" w="100%" mb={4}>
          <Link fontSize={{ base: 'xs', sm: 'sm' }}>
            {t('auth.form.forgot-password')}
          </Link>
        </Stack>
        <Box w="100%">
          <Button
            type="submit"
            variant="brandGradient"
            w="100%"
            isLoading={loading}
          >
            {t('buttons.sign-in')}
          </Button>
        </Box>
        <Text fontSize={{ base: 'xs', sm: 'sm' }}>Or</Text>
        <Box w="100%">
          <NextLink href={'/register'} passHref>
            <Link fontWeight="bold" fontSize={'md'} variant="nohover">
              <Button
                mt={0}
                size="lg"
                w="100%"
                h={'41'}
                color="black"
                bg={useColorModeValue('gray.100', 'gray.200')}
                _hover={{
                  bg: useColorModeValue('gray.200', 'gray.300'),
                  color: 'black',
                  textDecoration: 'none',
                }}
              >
                {t('buttons.create-account')}
              </Button>
            </Link>
          </NextLink>
        </Box>
        {error && (
          <Alert status="error" borderRadius="md">
            <AlertIcon />
            <AlertDescription>{error.message}</AlertDescription>
          </Alert>
        )}
      </VStack>
    </form>
  )
}
